// NEW_FILE_JUSTIFIED: New route page for policy templates list — no existing PolicyTemplatesPage
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateTemplateDialog } from '@/components/control/CreateTemplateDialog';
import { Plus } from 'lucide-react';

export function PolicyTemplatesPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const templates = useQuery(api.superadmin.queries.listPolicyTemplates);
  const toggleActive = useMutation(api.superadmin.mutations.toggleTemplateActive);

  const isLoading = templates === undefined;

  const handleToggle = useCallback(
    async (templateId: string) => {
      try {
        await toggleActive({ templateId: templateId as Id<'policyTemplates'> }); // AS_JUSTIFIED: one-time Id cast from string _id
        toast.success('Template status updated');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to toggle';
        toast.error(msg);
      }
    },
    [toggleActive]
  );

  return (
    <div className="flex flex-col gap-0 p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-foreground">
          Policy Templates{' '}
          {templates && <span className="text-muted-foreground text-lg">({templates.length})</span>}
        </h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center px-6 py-2 border-b border-border bg-muted/30 text-xs font-medium uppercase text-muted-foreground">
          <span className="w-56">Name</span>
          <span className="w-40">Policy Type</span>
          <span className="w-32">Provider</span>
          <span className="w-20 text-center">Fields</span>
          <span className="w-24 text-center">Assigned</span>
          <span className="w-24 text-center">Active</span>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          {isLoading && (
            <>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center px-6 py-3 border-b border-border gap-4">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-12" />
                </div>
              ))}
            </>
          )}
          {!isLoading && templates.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground">
              No templates. Create your first one.
            </div>
          )}
          {!isLoading &&
            templates.map(template => (
              <div
                key={template._id}
                className="flex items-center px-6 py-3 border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/control/templates/${template._id}`)}
              >
                <span className="w-56 font-medium text-sm truncate">{template.name}</span>
                <span className="w-40 text-sm">{template.policyTypeName}</span>
                <span className="w-32 text-sm">{template.providerName}</span>
                <span className="w-20 text-center text-sm">{template.fieldsCount}</span>
                <span className="w-24 text-center">
                  <Badge variant="outline" className="text-xs">
                    {template.assignedOrgCount} orgs
                  </Badge>
                </span>
                <span className="w-24 flex justify-center" onClick={e => e.stopPropagation()}>
                  <Switch
                    checked={template.isActive}
                    onCheckedChange={() => handleToggle(template._id)}
                  />
                </span>
              </div>
            ))}
        </div>
      </div>

      <CreateTemplateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
